import React, { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import logo from "../../assets/images/logo.png";
import { dashboard } from "utils/SideNavData";
import MenuItem from "utils/SideNavData";
import { Button, Tooltip } from "antd";
import { MenuOutlined, CloseOutlined, FileTextOutlined, PushpinOutlined } from "@ant-design/icons";
import userManual from "../../assets/docs/User Manual.pdf";

const HEADER_LINK_TEXT = process.env.REACT_APP_HEADER_LINK_TEXT || "iSekurex Capital";
const HEADER_LINK_TO = process.env.REACT_APP_HEADER_LINK_TO || "/tables";
const HEADER_LOGO_URL = process.env.REACT_APP_HEADER_LOGO_URL || "";

export default function SideNav({ color }) {
  // Sidebar starts open by default, persisted to localStorage
  const [isOpen, setIsOpen] = useState(() => {
    try {
      const v = localStorage.getItem("appverse:sidenav.open");
      return v ? JSON.parse(v) : true;
    } catch {
      return true;
    }
  });

  const [pinned, setPinned] = useState(() => {
    try {
      const v = localStorage.getItem("appverse:sidenav.pinned");
      return v ? JSON.parse(v) : false;
    } catch {
      return false;
    }
  });

  const menuRootRef = useRef(null);

  let { pathname } = useLocation();
  pathname = pathname.replace("/", "");

  useEffect(() => {
    try {
      localStorage.setItem("appverse:sidenav.open", JSON.stringify(isOpen));
    } catch {}
  }, [isOpen]);

  useEffect(() => {
    try { localStorage.setItem("appverse:sidenav.pinned", JSON.stringify(pinned)); } catch {}
  }, [pinned]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Keep main content pushed by the current sidenav width
  useEffect(() => {
    const width = isOpen ? 280 : 72;
    document.documentElement.style.setProperty("--sidenav-width", `${width}px`);
    document.body.classList.add("has-sidenav");
    return () => {
      document.body.classList.remove("has-sidenav");
    };
  }, [isOpen]);

  // Stamp readable labels on menu items for collapsed tooltips
  useEffect(() => {
    const root = menuRootRef.current;
    if (!root) return;
    const nodes = root.querySelectorAll('a, button, [role="menuitem"], .ant-menu-title-content');
    nodes.forEach((el) => {
      const text = (el.textContent || "").replace(/\s+/g, " ").trim();
      if (text) {
        el.setAttribute("data-label", text);
        if (!el.getAttribute("aria-label")) el.setAttribute("aria-label", text);
      }
    });
  }, [isOpen, pathname]);

  const toggleSidebar = () => {
    setIsOpen((v) => {
      const next = !v;
      if (!next && pinned) setPinned(false); // closing via X also unpins
      return next;
    });
  };

  const togglePin = () => {
    setPinned((p) => {
      const next = !p;
      if (next) setIsOpen(true); // pinning forces open
      return next;
    });
  };

  const sidebarStyles = {
    container: {
      width: isOpen ? "280px" : "72px",
      height: "100%",
      color: "#e5e7eb",
      position: "fixed",
      top: 0,
      left: 0,
      overflowX: "hidden",
      transition: "width 0.38s ease",
      padding: isOpen ? "20px" : "14px 10px",
      background:
        "linear-gradient(180deg, rgba(2,6,23,0.92), rgba(2,6,23,0.82)), " +
        "radial-gradient(120% 160% at -20% -20%, rgba(253,224,71,0.09), transparent 60%), " +
        "radial-gradient(160% 200% at 130% -30%, rgba(251,191,36,0.07), transparent 60%)",
      backdropFilter: "saturate(1.05) blur(8px)",
      WebkitBackdropFilter: "saturate(1.05) blur(8px)",
      borderRight: "1px solid rgba(253,224,71,0.24)",
      boxShadow: "0 20px 40px rgba(251,191,36,0.10)",
      zIndex: 1000,
      borderRadius: "0 16px 16px 0",
    },
    brand: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "30px",
      textDecoration: "none",
    },
    logo: {
      width: "22px",
      height: "22px",
      marginBottom: 0,
      objectFit: "contain",
      borderRadius: "4px",
    },
    hr: {
      borderColor: "rgba(253,224,71,0.22)",
      margin: "20px 0",
    },
    footerBox: {
      marginTop: "auto",
      padding: "16px",
      borderRadius: "14px",
      textAlign: "center",
      background:
        "linear-gradient(180deg, rgba(2,6,23,0.72), rgba(2,6,23,0.58)), " +
        "radial-gradient(120% 160% at -20% -20%, rgba(253,224,71,0.10), transparent 60%)",
      border: "1px solid rgba(251,191,36,0.22)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06), 0 10px 22px rgba(251,191,36,0.10)",
      marginBottom: "20px",
    },
    footerText: {
      color: "#b0bec5",
    },
    button: {
      width: "100%",
      marginTop: "12px",
      background:
        "linear-gradient(180deg, rgba(253,224,71,0.96), rgba(234,179,8,0.96))",
      border: "1px solid rgba(251,191,36,0.55)",
      color: "#0b0b12",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.35), 0 10px 22px rgba(251,191,36,0.18)",
    },
    menuItem: {
      padding: "10px 15px",
      margin: "15px 0",
      display: "flex",
      alignItems: "center",
      borderRadius: "5px",
      transition: "background-color 0.3s",
      cursor: "pointer",
    },
    menuItemSpecialSpacing: {
      marginBottom: "30px",
    },
    closeButton: {
      position: "fixed",
      top: "15px",
      left: "15px",
      zIndex: 1002,
      color: "#ffffff",
      background:
        "linear-gradient(180deg, rgba(24,24,27,0.85), rgba(24,24,27,0.65)), " +
        "radial-gradient(140% 180% at -20% -40%, rgba(253,224,71,0.25), transparent 60%)",
      padding: "6px",
      borderRadius: "8px",
      fontSize: "20px",
      border: "1px solid rgba(253,224,71,0.35)",
      boxShadow: "0 8px 22px rgba(253,224,71,0.18)",
      display: isOpen ? "inline-block" : "none",
      cursor: "pointer",
    },
    toggleMenu: {
      position: "fixed",
      top: "15px",
      left: "15px",
      zIndex: 1002,
      color: "#ffffff",
      background:
        "linear-gradient(180deg, rgba(24,24,27,0.85), rgba(24,24,27,0.65)), " +
        "radial-gradient(140% 180% at -20% -40%, rgba(253,224,71,0.25), transparent 60%)",
      padding: "6px",
      borderRadius: "8px",
      fontSize: "20px",
      border: "1px solid rgba(253,224,71,0.35)",
      boxShadow: "0 8px 22px rgba(253,224,71,0.18)",
      display: isOpen ? "none" : "inline-block",
      cursor: "pointer",
    },
    overlay: {
      display: isOpen && !pinned ? "block" : "none",
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 999,
    },
    pinButton: {
      position: "fixed",
      top: "68px",
      left: "15px",
      zIndex: 1002,
      color: "#ffffff",
      background:
        "linear-gradient(180deg, rgba(24,24,27,0.85), rgba(24,24,27,0.65)), " +
        "radial-gradient(140% 180% at -20% -40%, rgba(253,224,71,0.25), transparent 60%)",
      padding: "6px",
      borderRadius: "8px",
      fontSize: "18px",
      border: "1px solid rgba(253,224,71,0.35)",
      boxShadow: "0 8px 22px rgba(253,224,71,0.18)",
      display: "inline-block",
      cursor: "pointer",
    },
  };

  const brandNameStyle = {
    margin: 0,
    fontWeight: 700,
    letterSpacing: "0.2px",
    fontSize: "clamp(14px, 2.1vw, 18px)",
    lineHeight: 1.2,
    backgroundImage:
      "linear-gradient(92deg, #f8fafc 0%, #fde68a 22%, #fbbf24 44%, #fde68a 66%, #f8fafc 100%)",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
    backgroundSize: "200% 100%",
    backgroundPosition: "0% 0%",
    filter: "drop-shadow(0 1px 0 rgba(255,255,255,0.12)) drop-shadow(0 2px 6px rgba(251,191,36,0.22))",
    transition: "background-position 1.1s ease, filter .3s ease",
  };

  return (
    <>
      <style>{`
        /* Compact footer actions: stacked FABs in rail mode */
        .appverse-sidenav .fab-stack{ position:absolute; bottom:16px; left:50%; transform:translateX(-50%); display:flex; flex-direction:column; gap:12px; align-items:center; }
        .appverse-sidenav .fab{ width:44px; height:44px; border-radius:999px; display:flex; align-items:center; justify-content:center; background: linear-gradient(180deg, rgba(253,224,71,0.96), rgba(234,179,8,0.96)); border: 1px solid rgba(251,191,36,0.55); color:#0b0b12; box-shadow: inset 0 1px 0 rgba(255,255,255,0.35), 0 10px 22px rgba(251,191,36,0.18); }
        .appverse-sidenav .fab:hover{ filter:saturate(1.06) brightness(1.04); box-shadow: 0 12px 26px rgba(251,191,36,0.24); }
        .appverse-sidenav .fab-pin{ background: linear-gradient(180deg, rgba(24,24,27,0.85), rgba(24,24,27,0.65)); color:#e5e7eb; border: 1px solid rgba(253,224,71,0.35); }
        /* — SideNav luxe gold interactions — */
        .appverse-sidenav .brand-name{background-size:200% 100%; position:relative; display:inline-block;}
        .appverse-sidenav .brand-name:hover{background-position:100% 0; filter:saturate(1.06) brightness(1.03);}    
        .appverse-sidenav .brand-name::after{
          content:""; position:absolute; inset:0 auto 0 -160%;
          width:60%; pointer-events:none;
          background: linear-gradient(115deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.28) 46%, rgba(255,255,255,0) 100%);
          transform: skewX(-18deg);
          transition: left 1.1s ease, opacity 1.1s ease;
          opacity: .0;
        }
        .appverse-sidenav .brand-name:hover::after{ left:160%; opacity:.95; }

        .sidenav-toggle:hover{ transform: translateY(-1px); filter: saturate(1.05) brightness(1.04); box-shadow: 0 6px 14px rgba(251,191,36,0.18); }
        .sidenav-close:hover{ transform: translateY(-1px); filter: saturate(1.05) brightness(1.04); box-shadow: 0 6px 14px rgba(251,191,36,0.18); }
        .sidenav-pin:hover{ transform: translateY(-1px); filter: saturate(1.05) brightness(1.04); box-shadow: 0 6px 14px rgba(251,191,36,0.18); }

        /* Readability defaults for unselected items */
        .appverse-sidenav .menu-item-wrap{ 
          color: rgba(241,245,249,0.94); 
          font-weight: 500;
          text-shadow: 0 1px 1px rgba(0,0,0,0.35);
          transition: transform .18s ease, box-shadow .18s ease, background .18s ease, filter .18s ease, color .18s ease;
          border-radius: 10px;
        }
        .appverse-sidenav .menu-item-wrap a{ color: inherit; text-decoration: none; }
        .appverse-sidenav .menu-item-wrap svg{ color: inherit; opacity: 0.95; }

        /* Active states (common classnames used by routers/menus) */
        .appverse-sidenav .menu-item-wrap .active,
        .appverse-sidenav .menu-item-wrap .selected,
        .appverse-sidenav .menu-item-wrap [aria-current="page"],
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected{
          color: #ffffff;
          text-shadow: 0 1px 2px rgba(0,0,0,0.38);
        }

        /* Hover treatment aligned to the gold theme */
        .appverse-sidenav .menu-item-wrap:hover{ 
          transform: translateY(-1px); 
          filter:saturate(1.04); 
          background: linear-gradient(180deg, rgba(253,224,71,0.10), rgba(253,224,71,0.03)); 
          box-shadow: inset 0 0 0 1px rgba(251,191,36,0.28), 0 6px 14px rgba(251,191,36,0.12); 
          color: #ffffff;
        }

        /* Strengthen readability for generic text elements inside menu wrapper */
        .appverse-sidenav .menu-item-wrap h1,
        .appverse-sidenav .menu-item-wrap h2,
        .appverse-sidenav .menu-item-wrap h3,
        .appverse-sidenav .menu-item-wrap h4,
        .appverse-sidenav .menu-item-wrap h5,
        .appverse-sidenav .menu-item-wrap h6,
        .appverse-sidenav .menu-item-wrap .title,
        .appverse-sidenav .menu-item-wrap .label,
        .appverse-sidenav .menu-item-wrap > span {
          color: rgba(248,250,252,0.96) !important;
          text-shadow: 0 1px 1px rgba(0,0,0,0.35);
        }
        .appverse-sidenav .menu-item-wrap a:not(.ant-btn):not([aria-current="page"]) {
          color: rgba(248,250,252,0.96) !important;
        }

        /* Primary button gold theme + hover */
        .appverse-sidenav .ant-btn-primary{
          background: linear-gradient(180deg, rgba(253,224,71,0.96), rgba(234,179,8,0.96));
          border-color: rgba(251,191,36,0.55);
          color: #0b0b12;
        }
        .appverse-sidenav .ant-btn-primary:hover{
          filter: saturate(1.06) brightness(1.04);
          box-shadow: 0 10px 22px rgba(251,191,36,0.24);
        }

        /* — Luxe gold variables — */
        .appverse-sidenav{ --gold-1:#fde68a; --gold-2:#fbbf24; --gold-3:rgba(251,191,36,0.28); --panel:#0b1320; }

        /* Right-edge shimmer line */
        .appverse-sidenav::after{ content:""; position:absolute; top:8px; bottom:8px; right:-1px; width:1px; pointer-events:none; background:linear-gradient(180deg, rgba(253,224,71,0), rgba(253,224,71,0.6), rgba(253,224,71,0)); filter:blur(.2px); }

        /* Carded active state using :has for modern browsers; gracefully ignored elsewhere */
        .appverse-sidenav .menu-item-wrap:has([aria-current="page"]){
          background: linear-gradient(180deg, rgba(253,224,71,0.14), rgba(253,224,71,0.05));
          box-shadow: inset 0 0 0 1px rgba(251,191,36,0.35), 0 10px 24px rgba(251,191,36,0.12);
          color:#fff;
        }

        /* Icon polish without shifting layout */
        .appverse-sidenav .menu-item-wrap svg{ filter: drop-shadow(0 2px 6px rgba(251,191,36,0.16)); }
        .appverse-sidenav .menu-item-wrap:hover svg{ filter: drop-shadow(0 3px 10px rgba(251,191,36,0.26)); }

        /* Close icon hover */
        .appverse-sidenav .anticon svg{ transition: transform .18s ease, filter .18s ease; }
        .appverse-sidenav .anticon:hover svg{ transform: scale(1.06); filter: drop-shadow(0 6px 12px rgba(251,191,36,0.22)); }

        /* Divider glow */
        .appverse-sidenav hr{ border-color: rgba(253,224,71,0.28) !important; box-shadow: 0 1px 0 rgba(253,224,71,0.10); }

        /* Focus-visible ring for keyboard users */
        .appverse-sidenav a:focus-visible, .appverse-sidenav button:focus-visible{
          outline: none;
          box-shadow: 0 0 0 3px rgba(253,224,71,0.28), 0 0 0 1px rgba(251,191,36,0.55) inset;
          border-radius: 10px;
        }

        /* Subtle noise overlay for depth (no external asset; uses gradient dithering) */
        .appverse-sidenav{
          background-image:
            radial-gradient(1px 1px at 10% 20%, rgba(255,255,255,0.02), transparent 100%),
            radial-gradient(1px 1px at 80% 10%, rgba(255,255,255,0.02), transparent 100%),
            radial-gradient(1px 1px at 30% 70%, rgba(255,255,255,0.02), transparent 100%),
            radial-gradient(1px 1px at 60% 50%, rgba(255,255,255,0.02), transparent 100%),
            linear-gradient(180deg, rgba(2,6,23,0.92), rgba(2,6,23,0.82)),
            radial-gradient(120% 160% at -20% -20%, rgba(253,224,71,0.09), transparent 60%),
            radial-gradient(160% 200% at 130% -30%, rgba(251,191,36,0.07), transparent 60%) !important;
        }

        /* Mini‑rail collapsed mode */
        .appverse-sidenav.collapsed{ width:72px; }
        .appverse-sidenav.collapsed .brand-name{ opacity:0; width:0; margin:0; overflow:hidden; pointer-events:none; }
        .appverse-sidenav.collapsed hr{ margin:12px 0; }
        .appverse-sidenav.collapsed .menu-item-wrap{ padding:8px; margin:10px 0; text-align:center; }
        .appverse-sidenav.collapsed .menu-item-wrap :where(span, p, h6, h5){
          max-width:0; opacity:0; transform:translateX(-4px); overflow:hidden; display:inline-block;
        }
        .appverse-sidenav.collapsed .menu-item-wrap svg{ display:block; margin:0 auto; }
        .appverse-sidenav.collapsed .footerBox{ display:none !important; }

        /* Idle luxe brand shimmer (gentle) */
        @keyframes brandSheen{ 0%{background-position:0% 0} 50%{background-position:100% 0} 100%{background-position:0% 0} }
        .appverse-sidenav:not(.collapsed) .brand-name{ animation: brandSheen 8s ease-in-out infinite; }

        /* Indigent & Admin: luxe gold accent (robust across link/span/submenu) */
        /* Route match helpers using :has to catch anchors, NavLink(to), or custom data-route */
        .appverse-sidenav .menu-item-wrap:has([href*="/indigent"]),
        .appverse-sidenav .menu-item-wrap:has([to*="/indigent"]),
        .appverse-sidenav .menu-item-wrap:has([data-route*="/indigent"]),
        .appverse-sidenav .menu-item-wrap:has([href*="indigents"]),
        .appverse-sidenav .menu-item-wrap:has([to*="indigents"]),
        .appverse-sidenav .menu-item-wrap:has([data-route*="indigents"]),
        .appverse-sidenav .menu-item-wrap:has([href*="/admin"]),
        .appverse-sidenav .menu-item-wrap:has([to*="/admin"]),
        .appverse-sidenav .menu-item-wrap:has([data-route*="/admin"]) {
          /* base readability */
          color: rgba(248,250,252,0.96);
        }

        /* Apply gold gradient to the visible label text inside those matched items */
        .appverse-sidenav .menu-item-wrap:has([href*="/indigent"]):is(*) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([to*="/indigent"]):is(*) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([data-route*="/indigent"]):is(*) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([href*="indigents"]):is(*) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([to*="indigents"]):is(*) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([data-route*="indigents"]):is(*) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([href*="/admin"]):is(*) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([to*="/admin"]):is(*) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([data-route*="/admin"]):is(*) :where(.ant-menu-title-content, a, span, .label) {
          font-weight: 600;
          background-image: linear-gradient(92deg, #fde68a 0%, #fbbf24 50%, #fde68a 100%);
          -webkit-background-clip: text; background-clip: text;
          color: transparent !important; -webkit-text-fill-color: transparent;
          text-shadow: 0 1px 1px rgba(0,0,0,0.35);
        }

        /* Icon tint for those links/items */
        .appverse-sidenav .menu-item-wrap:has([href*="/indigent"]) svg,
        .appverse-sidenav .menu-item-wrap:has([to*="/indigent"]) svg,
        .appverse-sidenav .menu-item-wrap:has([data-route*="/indigent"]) svg,
        .appverse-sidenav .menu-item-wrap:has([href*="indigents"]) svg,
        .appverse-sidenav .menu-item-wrap:has([to*="indigents"]) svg,
        .appverse-sidenav .menu-item-wrap:has([data-route*="indigents"]) svg,
        .appverse-sidenav .menu-item-wrap:has([href*="/admin"]) svg,
        .appverse-sidenav .menu-item-wrap:has([to*="/admin"]) svg,
        .appverse-sidenav .menu-item-wrap:has([data-route*="/admin"]) svg{
          color: #fbbf24; filter: drop-shadow(0 2px 6px rgba(251,191,36,0.16));
        }
        .appverse-sidenav .menu-item-wrap:hover:has([href*="/indigent"]) svg,
        .appverse-sidenav .menu-item-wrap:hover:has([to*="/indigent"]) svg,
        .appverse-sidenav .menu-item-wrap:hover:has([data-route*="/indigent"]) svg,
        .appverse-sidenav .menu-item-wrap:hover:has([href*="indigents"]) svg,
        .appverse-sidenav .menu-item-wrap:hover:has([to*="indigents"]) svg,
        .appverse-sidenav .menu-item-wrap:hover:has([data-route*="indigents"]) svg,
        .appverse-sidenav .menu-item-wrap:hover:has([href*="/admin"]) svg,
        .appverse-sidenav .menu-item-wrap:hover:has([to*="/admin"]) svg,
        .appverse-sidenav .menu-item-wrap:hover:has([data-route*="/admin"]) svg{
          filter: drop-shadow(0 3px 10px rgba(251,191,36,0.26));
        }

        /* Active/selected states — force solid white for clarity regardless of element type */
        .appverse-sidenav .menu-item-wrap:has([href*="/indigent"][aria-current="page"]),
        .appverse-sidenav .menu-item-wrap:has([to*="/indigent"][aria-current="page"]),
        .appverse-sidenav .menu-item-wrap:has([data-route*="/indigent"][aria-current="page"]),
        .appverse-sidenav .menu-item-wrap:has([href*="indigents"][aria-current="page"]),
        .appverse-sidenav .menu-item-wrap:has([to*="indigents"][aria-current="page"]),
        .appverse-sidenav .menu-item-wrap:has([data-route*="indigents"][aria-current="page"]),
        .appverse-sidenav .menu-item-wrap:has([href*="/admin"][aria-current="page"]),
        .appverse-sidenav .menu-item-wrap:has([to*="/admin"][aria-current="page"]),
        .appverse-sidenav .menu-item-wrap:has([data-route*="/admin"][aria-current="page"]),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([href*="/indigent"]),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([to*="/indigent"]),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([data-route*="/indigent"]),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([href*="indigents"]),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([to*="indigents"]),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([data-route*="indigents"]),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([href*="/admin"]),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([to*="/admin"]),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([data-route*="/admin"]) {
          color:#fff !important;
        }
        .appverse-sidenav .menu-item-wrap:has([href*="/indigent"][aria-current="page"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([to*="/indigent"][aria-current="page"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([data-route*="/indigent"][aria-current="page"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([href*="indigents"][aria-current="page"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([to*="indigents"][aria-current="page"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([data-route*="indigents"][aria-current="page"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([href*="/admin"][aria-current="page"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([to*="/admin"][aria-current="page"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap:has([data-route*="/admin"][aria-current="page"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([href*="/indigent"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([to*="/indigent"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([data-route*="/indigent"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([href*="indigents"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([to*="indigents"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([data-route*="indigents"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([href*="/admin"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([to*="/admin"]) :where(.ant-menu-title-content, a, span, .label),
        .appverse-sidenav .menu-item-wrap .ant-menu-item-selected:has([data-route*="/admin"]) :where(.ant-menu-title-content, a, span, .label){
          background-image:none; -webkit-text-fill-color:#fff; color:#fff !important; text-shadow: 0 1px 2px rgba(0,0,0,0.38);
        }
        /* Collapsed tooltips so the rail is meaningful */
        .appverse-sidenav.collapsed .menu-item-wrap :is(a, button, [role="menuitem"], .ant-menu-title-content){ position: relative; }
        .appverse-sidenav.collapsed .menu-item-wrap :is(a, button, [role="menuitem"], .ant-menu-title-content)[data-label]::after{
          content: attr(data-label);
          position: absolute; left: calc(100% + 12px); top: 50%; transform: translateY(-50%);
          white-space: nowrap; z-index: 1003; pointer-events: none;
          background:
            linear-gradient(180deg, rgba(11,19,32,0.96), rgba(11,19,32,0.92)),
            radial-gradient(120% 160% at -20% -20%, rgba(253,224,71,0.12), transparent 60%);
          border: 1px solid rgba(251,191,36,0.38);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.10), 0 8px 18px rgba(251,191,36,0.16);
          border-radius: 10px; padding: 6px 10px; color: #f8fafc; opacity: 0; transition: opacity .18s ease;
        }
        .appverse-sidenav.collapsed .menu-item-wrap :is(a, button, [role="menuitem"], .ant-menu-title-content)[data-label]:hover::after{ opacity: 1; }
        .appverse-sidenav.collapsed .menu-item-wrap :is(a, button, [role="menuitem"], .ant-menu-title-content)[data-label]::before{
          content: ""; position: absolute; left: calc(100% + 6px); top: 50%; transform: translateY(-50%);
          width: 6px; height: 6px; border-radius: 2px; rotate: 45deg;
          background: rgba(11,19,32,0.96); border: 1px solid rgba(251,191,36,0.38); border-left: none; border-top: none; opacity: 0; transition: opacity .18s ease;
        }
        .appverse-sidenav.collapsed .menu-item-wrap :is(a, button, [role="menuitem"], .ant-menu-title-content)[data-label]:hover::before{ opacity: 1; }

        /* Active indicator in rail mode */
        .appverse-sidenav.collapsed .menu-item-wrap :is(a[aria-current="page"], .ant-menu-item-selected){ position: relative; }
        .appverse-sidenav.collapsed .menu-item-wrap :is(a[aria-current="page"], .ant-menu-item-selected) svg{ position: relative; }
        .appverse-sidenav.collapsed .menu-item-wrap :is(a[aria-current="page"], .ant-menu-item-selected) svg::after{
          content: ""; position: absolute; right: -6px; top: 50%; transform: translateY(-50%);
          width: 4px; height: 18px; border-radius: 2px; background: linear-gradient(180deg, #fde68a, #fbbf24);
        }
        /* Active icon glow in all modes */
        .appverse-sidenav .menu-item-wrap :is(a[aria-current="page"], .ant-menu-item-selected) svg{
          filter: drop-shadow(0 0 10px rgba(251,191,36,0.35));
        }

        /* Push main content by the current sidenav width (covers generic Ant Layout too) */
        body.has-sidenav .appverse-content{ margin-left: var(--sidenav-width, 72px); transition: margin-left .38s ease; }
        body.has-sidenav .ant-layout-header,
        body.has-sidenav .ant-layout-content:not(.appverse-content){ margin-left: var(--sidenav-width, 72px); transition: margin-left .38s ease; }

        /* Remove any left padding so content is flush with the sidenav */
        body.has-sidenav .appverse-content,
        body.has-sidenav .ant-layout-header,
        body.has-sidenav .ant-layout-content{
          padding-left: 0 !important;
        }
        /* Neutralise Ant Row/Col default gutters on the very first content container */
        body.has-sidenav .appverse-content > .ant-row{
          margin-left: 0 !important;
          margin-right: 0 !important;
        }
        body.has-sidenav .appverse-content > .ant-row > .ant-col{
          padding-left: 0 !important;
          padding-right: 0 !important;
        }

        @media (max-width: 767px){
          body.has-sidenav .appverse-content,
          body.has-sidenav .ant-layout-header,
          body.has-sidenav .ant-layout-content:not(.appverse-content){ margin-left: var(--sidenav-width, 72px); }
        }
      `}</style>

      {/* Overlay for dimming background */}
      <div style={sidebarStyles.overlay} onClick={toggleSidebar}></div>

      {/* Toggle Button for Small Screens */}
      <Button
        icon={<MenuOutlined style={{ fontSize: "20px" }} />}
        onClick={toggleSidebar}
        style={sidebarStyles.toggleMenu}
        className="sidenav-toggle"
      />
      <Button
        icon={<PushpinOutlined rotate={pinned ? 0 : 45} style={{ fontSize: "18px" }} />}
        onClick={togglePin}
        style={sidebarStyles.pinButton}
        className="sidenav-pin"
      />

      <div
        className={`appverse-sidenav ${!isOpen ? "collapsed" : ""}`}
        style={sidebarStyles.container}
      >
        {/* Close Icon for Sidebar */}
        <Button
          icon={<CloseOutlined style={{ fontSize: "20px" }} />}
          onClick={toggleSidebar}
          style={sidebarStyles.closeButton}
          className="sidenav-close"
        />

        <div style={sidebarStyles.brand} className="appverse-sidenav-brand">
          <Link
            to={HEADER_LINK_TO}
            aria-label={HEADER_LINK_TEXT}
            style={{ display: "flex", alignItems: "center", gap: 10, color: "inherit", textDecoration: "none" }}
          >
            <img src={HEADER_LOGO_URL || logo} alt="Logo" className="brand-mark" style={sidebarStyles.logo} />
            <span className="brand-name" style={brandNameStyle}>{HEADER_LINK_TEXT}</span>
          </Link>
        </div>
        <hr style={sidebarStyles.hr} />

        {/* Menu Items with Custom Spacing */}
        <div
          ref={menuRootRef}
          className="menu-item-wrap"
          style={{ ...sidebarStyles.menuItem, ...sidebarStyles.menuItemSpecialSpacing }}
        >
          <MenuItem pathname={pathname} color={color} />
        </div>

        <div style={sidebarStyles.footerBox} className="footerBox">
          <span className="icon" style={{ color: "red" }}>
            {dashboard(color)}
          </span>
          <h6>Need Help ?</h6>
          <p style={sidebarStyles.footerText}>Please check our docs</p>
          <Button
            type="primary"
            style={sidebarStyles.button}
            href={userManual}
            target="_blank"
            rel="noopener noreferrer"
          >
            DOCUMENTATION
          </Button>
        </div>
        {/* Render compact stacked footer actions when collapsed */}
        {!isOpen && (
          <div className="fab-stack">
            <Tooltip title={pinned ? "Unpin sidebar" : "Pin sidebar"} placement="right">
              <Button onClick={togglePin} className="fab fab-pin" aria-label="Toggle pin" icon={<PushpinOutlined rotate={pinned ? 0 : 45} />} />
            </Tooltip>
            <Tooltip title="Documentation" placement="right">
              <a href={userManual} target="_blank" rel="noopener noreferrer" aria-label="Open documentation" className="fab fab-docs">
                <FileTextOutlined style={{ fontSize: 20 }} />
              </a>
            </Tooltip>
          </div>
        )}
      </div>
    </>
  );
}